import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Property {
  id: number;
  title: string;
  location: string;
  price: string;
  type: string;
  bedrooms: number;
  bathrooms: number;
  area: string;
  lat: number;
  lng: number;
  image: string;
  description: string;
}

interface PropertiesMapProps {
  properties: Property[];
}

const MAP_CENTER_LAT = 38.35;
const MAP_CENTER_LNG = -0.48;

// Convierte lat/lng a posición porcentual dentro del contenedor del mapa
function latLngToPercent(lat: number, lng: number) {
  const minLat = 37.7;
  const maxLat = 38.85;
  const minLng = -0.85;
  const maxLng = 0.05;

  const x = ((lng - minLng) / (maxLng - minLng)) * 100;
  const y = ((maxLat - lat) / (maxLat - minLat)) * 100;

  return { x, y };
}

export default function PropertiesMap({ properties }: PropertiesMapProps) {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState<number | null>(null);

  const activeProperty = properties.find(p => p.id === activeId) || null;

  return (
    <section id="mapa-propiedades" className="py-20 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <span className="inline-block bg-red-50 text-red-600 px-6 py-2 rounded-full text-sm font-semibold mb-6">
            Mapa de Propiedades
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            📍 Encuentra propiedades cerca de ti
          </h2>
          <p className="text-base sm:text-xl text-gray-500 max-w-2xl mx-auto">
            Explora todas nuestras propiedades disponibles en la provincia de Alicante
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 items-start">

          {/* Lista lateral */}
          <div className="lg:col-span-1 flex flex-col gap-3 max-h-[560px] overflow-y-auto pr-1">
            {properties.map(p => (
              <button
                key={p.id}
                onClick={() => setActiveId(activeId === p.id ? null : p.id)}
                className={`w-full text-left rounded-2xl border transition-all duration-200 cursor-pointer overflow-hidden flex gap-0 group ${
                  activeId === p.id
                    ? 'border-red-500 shadow-lg bg-red-50'
                    : 'border-gray-100 bg-white hover:border-red-300 hover:shadow-md'
                }`}
              >
                <div className="w-24 h-24 flex-shrink-0 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="flex-1 p-3 min-w-0">
                  <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full mb-1 ${
                    activeId === p.id ? 'bg-red-600 text-white' : 'bg-red-100 text-red-600'
                  }`}>
                    {p.type}
                  </span>
                  <p className="text-sm font-bold text-gray-900 leading-tight truncate">{p.title}</p>
                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5 truncate">
                    <i className="ri-map-pin-line text-red-500 flex-shrink-0"></i>
                    {p.location}
                  </p>
                  <p className="text-sm font-semibold text-red-600 mt-1">{p.price}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Mapa interactivo */}
          <div className="lg:col-span-2">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-gray-100" style={{ height: '560px' }}>
              {/* Google Maps embed centrado en Alicante */}
              <iframe
                title="Mapa de propiedades Red Lions Realty - Alicante"
                src={`https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d200000!2d${MAP_CENTER_LNG}!3d${MAP_CENTER_LAT}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1ses!2ses!4v1700000000001!5m2!1ses!2ses`}
                width="100%"
                height="100%"
                style={{ border: 0, display: 'block' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>

              {/* Pins superpuestos */}
              <div className="absolute inset-0 pointer-events-none">
                {properties.map(p => {
                  const { x, y } = latLngToPercent(p.lat, p.lng);
                  const isActive = activeId === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setActiveId(activeId === p.id ? null : p.id)}
                      className="absolute pointer-events-auto cursor-pointer group"
                      style={{
                        left: `${x}%`,
                        top: `${y}%`,
                        transform: 'translate(-50%, -100%)',
                        zIndex: isActive ? 20 : 10,
                      }}
                    >
                      {/* Pin */}
                      <div className={`flex flex-col items-center transition-transform duration-200 ${isActive ? 'scale-125' : 'group-hover:scale-110'}`}>
                        <div className={`px-3 py-1.5 rounded-full text-xs font-bold shadow-lg whitespace-nowrap ${
                          isActive ? 'bg-red-600 text-white' : 'bg-white text-gray-900 border border-gray-200'
                        }`}>
                          {p.price === 'Consultar precio' ? 'Consultar' : p.price.replace('Desde ', '')}
                        </div>
                        <div className={`w-3 h-3 rotate-45 -mt-1.5 shadow ${
                          isActive ? 'bg-red-600' : 'bg-white border border-gray-200'
                        }`}></div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Popup de propiedad activa */}
              {activeProperty && (
                <div className="absolute bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 bg-white rounded-2xl shadow-2xl overflow-hidden z-30 animate-fade-in">
                  <div className="relative h-36 overflow-hidden">
                    <img
                      src={activeProperty.image}
                      alt={activeProperty.title}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <span className="absolute top-3 left-3 bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      {activeProperty.type}
                    </span>
                    <button
                      onClick={() => setActiveId(null)}
                      className="absolute top-3 right-3 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center cursor-pointer hover:bg-white transition-colors"
                    >
                      <i className="ri-close-line text-gray-700 text-sm"></i>
                    </button>
                  </div>
                  <div className="p-4">
                    <p className="text-lg font-bold text-red-600 mb-0.5">{activeProperty.price}</p>
                    <h3 className="text-base font-bold text-gray-900 mb-1 leading-tight">{activeProperty.title}</h3>
                    <p className="text-xs text-gray-500 flex items-center gap-1 mb-3">
                      <i className="ri-map-pin-line text-red-500"></i>
                      {activeProperty.location}
                    </p>
                    <div className="flex items-center gap-4 text-gray-600 text-xs mb-4">
                      <span className="flex items-center gap-1">
                        <i className="ri-hotel-bed-line text-red-500"></i>
                        {activeProperty.bedrooms} hab.
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-drop-line text-red-500"></i>
                        {activeProperty.bathrooms} baños
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-ruler-line text-red-500"></i>
                        {activeProperty.area}
                      </span>
                    </div>
                    <button
                      onClick={() => navigate(`/propiedad/${activeProperty.id}`)}
                      className="w-full bg-red-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-red-700 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                      Ver propiedad
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Leyenda */}
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-gray-500 px-1">
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 bg-red-600 rounded-full inline-block"></span>
                Propiedad seleccionada
              </span>
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 bg-white border border-gray-300 rounded-full inline-block"></span>
                Propiedad disponible
              </span>
              <span className="ml-auto text-xs text-gray-400">
                Haz clic en un pin o en la lista para ver detalles
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
